import { Form, Formik } from 'formik';
import { useEffect, useState, type MouseEvent } from 'react';
import { isEqual } from 'lodash';
import * as Yup from 'yup';
import { Banner } from '@packages/ui/layout';
import { Typography } from '@packages/ui/primitives';
import ModalComponent from '../../../../components/modal/modal';
import ModalBodyComponent from '../../../../components/modal/modalBodyComponent';
import ModalFooterComponent from '../../../../components/modal/modalFooterComponent';
import InputText from '../../../../components/input-text/inputText';
import Button from '../../../../components/button';
import Heading from '../../../../components/typography';
import Spinners from '../../../../components/spinner';
import { COLORS } from '../../../../constants/style/color';
import {
  buildEnrollmentStartUrl,
  normalizeAllowedHostsFromForm,
  parseAllowedHostEntry,
} from '../enrollmentConfigUtils';
import CustomToggle from '../../../../components/toggle';
import AllowedRedirectUrlHostsField from './AllowedRedirectUrlHostsField';

export interface AdvisorEnrollmentConfigFormValues {
  displayName: string;
  allowedReturnUrlHosts: string[];
  isActive: boolean;
}

interface AdvisorEnrollmentConfigModalProps {
  isOpen: boolean;
  isLoading: boolean;
  isSaving: boolean;
  companyName?: string;
  enrollmentKey?: string | null;
  initialValues: AdvisorEnrollmentConfigFormValues;
  onClose: () => void;
  onSubmit: (values: AdvisorEnrollmentConfigFormValues) => void;
}

const validationSchema = Yup.object().shape({
  displayName: Yup.string().trim().required('Display name is required'),
  allowedReturnUrlHosts: Yup.array()
    .of(Yup.string())
    .test(
      'hosts-required',
      'At least one allowed redirect host is required',
      (value) => normalizeAllowedHostsFromForm(value).length > 0,
    )
    .test(
      'hosts-valid',
      'Enter valid host names only (for example localhost or app.example.com)',
      (value) => {
        const entries = (value ?? []).filter((entry) => entry.trim().length > 0);
        if (entries.length === 0) {
          return false;
        }

        return entries.every((entry) => parseAllowedHostEntry(entry) != null);
      },
    ),
});

const EnrollmentKeyPanel = ({
  enrollmentKey,
  companyName,
  allowedReturnUrlHosts,
}: {
  enrollmentKey?: string | null;
  companyName?: string;
  allowedReturnUrlHosts: string[];
}): JSX.Element => {
  const [isCopied, setIsCopied] = useState(false);
  const [isUrlCopied, setIsUrlCopied] = useState(false);
  const hasKey = Boolean(enrollmentKey);

  useEffect(() => {
    setIsCopied(false);
    setIsUrlCopied(false);
  }, [enrollmentKey, allowedReturnUrlHosts]);

  const enrollmentStartUrl =
    hasKey ? buildEnrollmentStartUrl(enrollmentKey!, allowedReturnUrlHosts) : null;
  const companyLabel = companyName?.trim() || 'your company';

  const handleCopyKey = (event: MouseEvent<HTMLButtonElement>): void => {
    event.preventDefault();
    if (!enrollmentKey) return;
    void navigator.clipboard.writeText(enrollmentKey).then(() => {
      setIsCopied(true);
    });
  };

  const handleCopyUrl = (event: MouseEvent<HTMLButtonElement>): void => {
    event.preventDefault();
    if (!enrollmentStartUrl) return;
    void navigator.clipboard.writeText(enrollmentStartUrl).then(() => {
      setIsUrlCopied(true);
    });
  };

  return (
    <div
      className='rounded-lg px-4 py-3 mb-4'
      style={{ backgroundColor: COLORS.CulturedWhite }}>
      <Heading variant='body-xs' title='Enrollment key' className='detailLabel' />
      {hasKey ? (
        <>
          <div className='flex flex-wrap items-center gap-2 mt-1'>
            <Heading variant='body-s' title={enrollmentKey} />
            <Button
              id='copyEnrollmentKeyButton'
              type='button'
              title={isCopied ? 'Copied' : 'Copy key'}
              variant='primary-outline'
              icon={isCopied ? 'check_circle' : 'content_copy'}
              onClick={handleCopyKey}
            />
          </div>
          <Heading
            variant='body-xs'
            color={COLORS.SpanishGray}
            className='mt-2'
            title={`Use this key in enrollment links for ${companyLabel} — not your internal company ID. It stays the same after you save.`}
          />
          <Heading variant='body-xs' title='Enrollment start link (example)' className='detailLabel mt-4' />
          {enrollmentStartUrl ? (
            <>
              <div
                className='mt-2 rounded-md px-4 py-3 break-all'
                style={{ backgroundColor: COLORS.White, border: '1px solid #E4E4E7' }}>
                <Heading variant='h6' color={COLORS.Purple} title={enrollmentStartUrl} />
              </div>
              <div className='mt-2'>
                <Button
                  id='copyEnrollmentUrlButton'
                  type='button'
                  title={isUrlCopied ? 'Copied' : 'Copy example URL'}
                  variant='primary-outline'
                  icon={isUrlCopied ? 'check_circle' : 'content_copy'}
                  onClick={handleCopyUrl}
                />
              </div>
              <Heading
                variant='body-xs'
                color={COLORS.SpanishGray}
                className='mt-2'
                title='Uses your first allowed host as return_url (https://{host}). Replace state with a value your application generates for each enrollment attempt.'
              />
            </>
          ) : (
            <Heading
              variant='body-s'
              className='mt-2'
              color={COLORS.SpanishGray}
              title='Add at least one allowed redirect host above to preview an example enrollment link.'
            />
          )}
        </>
      ) : (
        <Heading
          variant='body-s'
          className='mt-1'
          title='No enrollment key yet. Save settings below to generate one automatically.'
        />
      )}
    </div>
  );
};

const AdvisorEnrollmentConfigModal = ({
  isOpen,
  isLoading,
  isSaving,
  companyName,
  enrollmentKey,
  initialValues,
  onClose,
  onSubmit,
}: AdvisorEnrollmentConfigModalProps): JSX.Element => {
  if (!isOpen) return <></>;

  const isBusy = isLoading || isSaving;
  const companyLabel = companyName?.trim() || 'your company';

  return (
    <ModalComponent
      id='advisorEnrollmentConfigModal'
      show={isOpen}
      closeModal={isBusy ? () => {} : onClose}
      title='Enrollment settings'
      subTitle={`Set up how participants enroll to share holdings with ${companyLabel}.`}
      size='lg'
      outsideClickable={false}>
      {isLoading ? (
        <ModalBodyComponent>
          <Spinners containerClassName='py-10' />
        </ModalBodyComponent>
      ) : (
        <Formik
          enableReinitialize
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={(values) => {
            onSubmit(values);
          }}>
          {({ touched, handleChange, errors, values, initialValues: formInitialValues }) => (
            <Form id='advisorEnrollmentConfigForm'>
              <ModalBodyComponent>
                <Banner variant='info' className='mb-4'>
                  <Typography variant='body-s'>
                    When you send participants to Wealthlane, they sign in and choose which issuer
                    companies to share with {companyLabel}. Configure your enrollment key, the name
                    participants see, and which callback hosts are allowed. API keys on this page
                    are for programmatic access after participants enroll.
                  </Typography>
                </Banner>

                <div className='mb-4'>
                  <CustomToggle
                    label='Enrollment enabled'
                    name='isActive'
                    id='advisorEnrollmentIsActive'
                    checked={values.isActive}
                    onChange={handleChange}
                  />
                  <Heading
                    variant='body-xs'
                    color={COLORS.SpanishGray}
                    className='mt-2'
                    title='When disabled, enrollment links stop working after you save. Participants will see the same error as when enrollment is not configured.'
                  />
                </div>

                {!values.isActive && (
                  <Banner variant='warning' className='mb-4'>
                    <Typography variant='body-s'>
                      Enrollment is turned off in this form. Save settings to apply — until then,
                      the current saved state still applies.
                    </Typography>
                  </Banner>
                )}

                <div className='grid grid-cols-1 gap-4'>
                  <InputText
                    label='Name shown to participants'
                    name='displayName'
                    onChange={handleChange}
                    errors={errors}
                    touched={touched}
                    required
                    placeholder={companyName?.trim() || 'Your company name'}
                    note={`Shown on enrollment pages so participants know ${companyLabel} is requesting access.`}
                  />
                  <AllowedRedirectUrlHostsField
                    values={values}
                    errors={errors}
                    touched={touched}
                    handleChange={handleChange}
                  />
                </div>

                <EnrollmentKeyPanel
                  enrollmentKey={enrollmentKey}
                  companyName={companyName}
                  allowedReturnUrlHosts={values.allowedReturnUrlHosts}
                />
              </ModalBodyComponent>
              <ModalFooterComponent
                onCloseModal={onClose}
                isEditShow={false}
                modalStatus='Edit'
                buttonTitle='Save settings'
                isLoading={isSaving}
                valueChanged={isEqual(values, formInitialValues)}
              />
            </Form>
          )}
        </Formik>
      )}
    </ModalComponent>
  );
};

export default AdvisorEnrollmentConfigModal;
